import React, { useMemo } from 'react'
import queryString from 'query-string'
import { useNavigate, useParams } from 'react-router-dom'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { DocumentObj } from 'src/adapter/AttachmentAdapter'
import { usePolicyDocumentsByIds } from 'src/pages/Policies/Policies.hooks'
import { policyDocRecordToDocumentObj } from 'src/pages/Policies/Policies.helpers'
import {
  Grid,
  GridColumn,
  GridHeader,
  GridRow,
} from 'src/components/Reusable/Grid'
import { NoResults } from 'src/components/PageLayout/NoResults'
import { NoPoliciesIcon } from 'src/components/Icons/EmptyStateIcons'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { formatTimestampObject } from 'src/Utils/formatDate'
import { generatePermalink } from 'src/components/PageLayout/Permalink'
import { getEvidenceMimeType } from 'src/pages/Evidence/evidence.helpers'
import { useFileTypeIconForEvidence } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { BrokenLinkIcon } from 'src/components/Icons/Basic/BrokenLinkIcon'
import { DocumentRow } from 'src/pages/Policies/PoliciesShowPage/DocumentGrid/DocumentItem/DocumentItem.styles'
import { DocumentSource } from 'src/pages/Policies/PoliciesShowPage/DocumentGrid/DocumentItem/DocumentItem.components'

const GRID_TEMPLATE_COLUMNS =
  'min-content minmax(14rem, 1fr) minmax(14rem, 1fr) max-content repeat(4, max-content)'

export const PolicyDocumentsTab = ({
  documentIds,
  policyNamesByModelId,
  emptyStateTitle,
  emptyStateNote,
}: {
  documentIds: string[]
  policyNamesByModelId: Record<string, string>
  emptyStateTitle: string
  emptyStateNote: string
}): JSX.Element => {
  // Scanned policy documents live in policy_documents (not the legacy
  // documents table), so resolve the ids through the Policy service.
  const { data, isLoading } = usePolicyDocumentsByIds(
    documentIds,
    documentIds.length > 0,
  )
  const sortedDocuments = useMemo(() => {
    const records = data?.getDocumentsList() || []
    return records
      .map(policyDocRecordToDocumentObj)
      .sort((a, b) => b.createdat - a.createdat)
  }, [data])

  if (isLoading) {
    return <TableListLoader rows={10} />
  }

  if (sortedDocuments.length === 0) {
    return (
      <NoResults
        icons={<NoPoliciesIcon />}
        title={emptyStateTitle}
        note={emptyStateNote}
      />
    )
  }

  return (
    <Grid style={{ width: '100%' }} gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
      <GridRow>
        <GridHeader>Type</GridHeader>
        <GridHeader>Policy</GridHeader>
        <GridHeader>Document</GridHeader>
        <GridHeader>Source</GridHeader>
        <GridHeader>Last Modified</GridHeader>
        <GridHeader>Last Checked</GridHeader>
        <GridHeader>Last Reviewed</GridHeader>
        <GridHeader>Last Approved</GridHeader>
      </GridRow>
      {sortedDocuments.map((document) => (
        <PolicyDocumentRow
          key={document.newInstanceId || document.id}
          document={document}
          policyNamesByModelId={policyNamesByModelId}
        />
      ))}
    </Grid>
  )
}

const PolicyDocumentRow = ({
  document,
  policyNamesByModelId,
}: {
  document: DocumentObj
  policyNamesByModelId: Record<string, string>
}): JSX.Element => {
  const { pageContext } = useParams()
  const navigate = useNavigate()
  const actualMime = getEvidenceMimeType(document.mime)
  const Icon = useFileTypeIconForEvidence(
    false,
    actualMime,
    document.isLinkedDocument,
  )
  const policyModelId = document.subjectmodelid
  // A freshly scanned document has no policy until the matching / wrapper-policy
  // step links one (normally within a minute or two).
  const policyName =
    policyNamesByModelId[policyModelId] || policyModelId || 'No Linked Policy'
  const policyLink = policyModelId
    ? queryString.stringifyUrl({
        url: generatePermalink({
          pageContext: pageContext as string,
          modelId: policyModelId,
          isInternalLink: true,
          includeSearchParams: false,
          modelType: MODEL_TYPE.POLICY,
        }),
        query: {
          document_id: document.id,
        },
      })
    : ''

  // Prefer the source's last-modified date (doc_modified_at); fall back to the
  // relevant date for manually uploaded docs that have no modified date.
  const lastModifiedDate = document.docModifiedAt ?? document.relevantdate
  const lastModified = lastModifiedDate
    ? formatTimestampObject(lastModifiedDate, DATE_FORMATS.ISO_WITH_TIME)
    : ''
  const lastChecked = document.docScannedAt
    ? formatTimestampObject(document.docScannedAt, DATE_FORMATS.ISO_WITH_TIME)
    : ''
  const lastReviewed = document.lastReviewedDate
    ? formatTimestampObject(document.lastReviewedDate, DATE_FORMATS.ISO, true)
    : ''
  const lastApproved = document.lastApprovedDate
    ? formatTimestampObject(document.lastApprovedDate, DATE_FORMATS.ISO, true)
    : ''

  return (
    <DocumentRow
      onClick={() => {
        if (policyLink) {
          navigate(policyLink)
        }
      }}
    >
      <GridColumn>
        {document.isLinkBroken ? (
          <BrokenLinkIcon />
        ) : (
          <Icon width="24px" height="24px" />
        )}
      </GridColumn>
      <GridColumn>{policyName}</GridColumn>
      <GridColumn>{document.caption}</GridColumn>
      <GridColumn>
        <DocumentSource document={document} />
      </GridColumn>
      <GridColumn>{lastModified}</GridColumn>
      <GridColumn>{lastChecked}</GridColumn>
      <GridColumn>{lastReviewed}</GridColumn>
      <GridColumn>{lastApproved}</GridColumn>
    </DocumentRow>
  )
}
